import { MoveAvroSchema, TrackingValidation } from './api';

export const status = {
  PASS: 'PASS',
  WARN: 'WARN',
  FAIL: 'FAIL',
  GLOBAL: 'GLOBAL',
  UNRECOGNIZED: 'UNRECOGNIZED',
};

export const flattenRequest = (obj: any) => {
  const flattened: any = {};
  Object.keys(obj).forEach((key: string) => {
    const val: any = obj[key];
    flattened[key] = val;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      const subFlattened = flattenRequest(val);
      Object.keys(subFlattened).forEach(subKey => {
        const subVal = subFlattened[subKey];
        flattened[`${key}.${subKey}`] = subVal;
      });
    }
  });

  return flattened;
};

export const transformGuidance = (guidance: any): any => {
  if (!Array.isArray(guidance)) {
    return {};
  }

  const guidanceMap: any = {};
  guidance.forEach((entry: any) => {
    guidanceMap[entry.fieldName] = entry;
  });

  return guidanceMap;
};

export const schemaString = (schema: any): string => {
  let expectedTypes: string = '';
  if (typeof schema === 'string') {
    return schema;
  }

  if (schema.symbols) {
    expectedTypes = `${schema.name} (valid values are: `;
    let firstSymbol = true;
    schema.symbols.forEach((symbol: string) => {
      if (!firstSymbol) {
        expectedTypes += ', ';
      }
      expectedTypes += `"${symbol}"`;
      firstSymbol = false;
    });
    expectedTypes += ')';
    return expectedTypes;
  }

  if (Array.isArray(schema.type)) {
    let firstType: boolean = true;
    schema.type.forEach((type: any) => {
      if (!firstType) {
        expectedTypes += ', ';
      }
      expectedTypes += schemaString(type);
      firstType = false;
    });
    return expectedTypes;
  }

  return schemaString(schema.type);
};

export const incorrectTypeString = (type: any, schema: any): string => {
  let expectedTypes: string = '';
  type.expectedType.forEach((item: string) => {
    if (expectedTypes !== '') {
      expectedTypes += ', ';
    }

    if (schema.name === item) {
      expectedTypes += schemaString(schema);
    } else {
      expectedTypes += item;
    }
  });

  const start = `Incorrect type of ${type.actualType} provided, however`;
  if (type.expectedType.length === 1) {
    return `${start} a ${expectedTypes} was expected.`;
  }
  return `${start} one of ${expectedTypes} were expected.`;
};

export const guidanceString = (guidance: any): string => {
  let reasons = '';
  guidance?.trackingValidation?.forEach((reason: any) => {
    if (reasons !== '') {
      reasons += ', ';
    }
    reasons += reason.message;
  });
  return reasons;
};

export const getStatusForProperty = (
  schema: any,
  guidance: any,
  incorrectType: any,
  value: any,
): { status: string; reason: null | string } => {
  if (schema !== undefined) {
    if (value === undefined) {
      return {
        status: status.FAIL,
        reason: `Property is expected, but not provided. A ${schemaString(schema)} is expected.`,
      };
    }

    if (incorrectType !== undefined) {
      return { status: status.FAIL, reason: incorrectTypeString(incorrectType, schema) };
    }

    if (guidance === undefined) {
      return { status: status.PASS, reason: null };
    }

    return { status: guidance.status, reason: guidanceString(guidance) };
  }

  if (guidance === undefined) {
    return { status: status.GLOBAL, reason: 'Property is a global' };
  }
  return { status: status.UNRECOGNIZED, reason: 'Property is unrecognized for this event' };
};

export const transformIncorrectTypes = (types: any) => {
  const ret: any = {};
  (types || []).forEach((type: any) => {
    ret[type.path] = type;
  });

  return ret;
};

export function flattenSchema(schema: MoveAvroSchema, prefix?: string): any {
  if (schema === undefined) {
    return {};
  }

  let pfx = prefix || '';
  if (pfx !== '') {
    pfx = `${pfx}.`;
  }

  let flattened: any = {};
  schema.fields?.forEach(field => {
    const name = pfx + field.name;
    flattened[name] = field;
    if (Array.isArray(field.type)) {
      field.type.forEach(subField => {
        if (subField !== null && subField.name !== undefined) {
          flattened = { ...flattened, ...flattenSchema(subField, name) };
        }
      });
    } else {
      flattened = { ...flattened, ...flattenSchema(field.type, name) };
    }
  });

  return flattened;
}

export function compressSchema(schema: MoveAvroSchema, prefix?: string): Map<string, TrackingValidation> {
  let fieldTrackingMap: Map<string, TrackingValidation> = new Map<string, TrackingValidation>();
  schema.fields?.forEach(field => {
    const fieldName = prefix ? `${prefix}.${field.name}` : field.name;
    const trackingValidation: TrackingValidation = field['tracking-validation'] ? field['tracking-validation'] : {};
    trackingValidation.isOptional = field.default !== undefined;
    fieldTrackingMap.set(fieldName as string, trackingValidation);
    // if `type` is an array
    if (Array.isArray(field.type)) {
      field.type.forEach(subField => {
        if (subField !== null && subField.name !== undefined) {
          fieldTrackingMap = new Map([
            ...fieldTrackingMap,
            ...compressSchema(subField as MoveAvroSchema, subField.name),
          ]);
        }
      });
    } else {
      fieldTrackingMap = new Map([...fieldTrackingMap, ...compressSchema(field.type as MoveAvroSchema, field.name)]);
    }
  });
  return fieldTrackingMap;
}

export function getParamDescriptionMap(schema: MoveAvroSchema): Map<string, string> {
  const output: Map<string, string> = new Map<string, string>();
  let listOfFields: MoveAvroSchema;
  if (schema.name === 'PageViewEvent') {
    listOfFields = schema.fields?.at(1)?.type as MoveAvroSchema;
  } else {
    listOfFields = schema.fields?.at(0)?.type as MoveAvroSchema;
  }
  listOfFields.fields?.forEach(value => {
    output.set(value.name ? value.name : '', value.doc ? value.doc : '');
  });
  return output;
}

export const PLATFORM_MAP = {
  web: 'prism.clickstream.DirectSendWebEvent',
  ios: 'prism.clickstream.DirectSendIosEvent',
  android: 'prism.clickstream.DirectSendAndroidEvent',
};

const GLOBAL_PARAMETERS = {
  // any flattened parameters with these prefixes are globals across all DS schemas
  prefixes: ['metadata.', 'decorators.', 'context.'],
  'prism.clickstream.DirectSendIosEvent': ['originalTimestamp', 'sentAt', 'receivedAt', 'type'],
  'prism.clickstream.DirectSendAndroidEvent': ['originalTimestamp', 'sentAt', 'receivedAt', 'type'],
  'prism.clickstream.DirectSendWebEvent': [
    'adobeMcid',
    'adobeS_vi',
    'adobeVisitorId',
    'appVersion',
    'bot',
    'brandExperience',
    'browserAcceptLanguage',
    'browserCookieEnabled',
    'browserCookieEnabledYn',
    'browserEncoding',
    'browserJavaEnabled',
    'browserName',
    'campaign',
    'clientId',
    'cm_sff',
    'contentId',
    'currentDate',
    'doubleClickKeywordId',
    'ecidLoadTime',
    'edwSessionId',
    'edwVisitorId',
    'fbc',
    'fbp',
    'fingerprint',
    'gclid',
    'hitId',
    'hostName',
    'identityId',
    'identityId',
    'iid',
    'isRdcx',
    'isRentalMonetized',
    'label',
    'locale',
    'loginStatus',
    'mid',
    'optimizelyAttributes',
    'os',
    'pagePath',
    'pageTitle',
    'pageUrl',
    'path',
    'performanceMetrics',
    'previousPageName',
    'previousPageName',
    'protocol',
    'publisher',
    'quantity',
    'queryParam',
    'referrer',
    'registeredUserActivity',
    'rid',
    'screenColorDepth',
    'screenHeight',
    'screenResolution',
    'screenWidth',
    'search',
    'threshold',
    'timeZone',
    'title',
    'url',
    'userAgent',
    'utcTimestamp',
    'webViewSessionId',
  ],
};

export function buildGlobalsData(flattenedSchema: any, schemaName: string): any[] {
  const globalsData: any[] = [];
  const globalParamNames = GLOBAL_PARAMETERS[schemaName as keyof typeof GLOBAL_PARAMETERS];

  Object.keys(flattenedSchema).forEach(paramName => {
    GLOBAL_PARAMETERS.prefixes.forEach(prefix => {
      if (paramName.startsWith(prefix)) {
        globalsData.push({ paramName: paramName, description: flattenedSchema[paramName].doc });
      }
    });

    const strippedParamName = paramName.replace('properties.', '');
    if (globalParamNames.includes(strippedParamName)) {
      globalsData.push({ paramName: paramName, description: flattenedSchema[paramName].doc });
    }
  });
  return globalsData;
}
